import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  CmsPerkResponse,
  CreateCmsPerkRequest,
  CmsUpdatePerkStatusRequest,
  CmsUploadPromoCodesRequest,
  CmsPromoCodeResponse,
  CmsRedemptionLogDto,
  PerkOptOutResponse,
  PerkStatus,
} from "@/types/generated/cms.generated";

// Fetch all global perks
export function usePerks() {
  return useQuery<CmsPerkResponse[]>({
    queryKey: ["perks"],
    queryFn: () => apiRequest("GET", "/perks"),
  });
}

// Fetch single perk detail
export function usePerk(perkId: string | null | undefined) {
  return useQuery<CmsPerkResponse>({
    queryKey: ["perks", perkId],
    queryFn: () => apiRequest("GET", `/perks/${perkId}`),
    enabled: !!perkId,
  });
}

// Create perk
export function useCreatePerk() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateCmsPerkRequest) =>
      apiRequest("POST", "/perks", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perks"] });
      toast({
        title: "Perk created",
        description: "The perk has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create perk. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Update perk
export function useUpdatePerk() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateCmsPerkRequest }) =>
      apiRequest("PUT", `/perks/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["perks"] });
      queryClient.invalidateQueries({ queryKey: ["perks", variables.id] });
      toast({
        title: "Perk updated",
        description: "The perk has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update perk. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Delete perk (soft-delete)
export function useDeletePerk() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/perks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["perks"] });
      toast({
        title: "Perk deleted",
        description: "The perk has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete perk. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Toggle perk status
export function useUpdatePerkStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: PerkStatus }) =>
      apiRequest("PUT", `/perks/${id}/status`, { status } as CmsUpdatePerkStatusRequest),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["perks"] });
      queryClient.invalidateQueries({ queryKey: ["perks", variables.id] });
      toast({
        title: "Status updated",
        description: `Perk status changed to ${variables.status}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update perk status.",
        variant: "destructive",
      });
    },
  });
}

// Upload promo codes
export function useUploadPromoCodes() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ perkId, data }: { perkId: string; data: CmsUploadPromoCodesRequest }) =>
      apiRequest("POST", `/perks/${perkId}/promo-codes`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["perks", variables.perkId, "promo-codes"] });
      queryClient.invalidateQueries({ queryKey: ["perks"] });
      toast({
        title: "Promo codes uploaded",
        description: "Promo codes have been uploaded successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload promo codes.",
        variant: "destructive",
      });
    },
  });
}

// Fetch promo codes for a perk
export function usePerkPromoCodes(perkId: string | null | undefined) {
  return useQuery<CmsPromoCodeResponse[]>({
    queryKey: ["perks", perkId, "promo-codes"],
    queryFn: () => apiRequest("GET", `/perks/${perkId}/promo-codes`),
    enabled: !!perkId,
  });
}

// Fetch redemption logs for a perk
export function usePerkRedemptions(perkId: string | null | undefined) {
  return useQuery<CmsRedemptionLogDto[]>({
    queryKey: ["perks", perkId, "redemptions"],
    queryFn: () => apiRequest("GET", `/perks/${perkId}/redemptions`),
    enabled: !!perkId,
  });
}

// Fetch opt-outs for a perk
export function usePerkOptOuts(perkId: string | null | undefined) {
  return useQuery<PerkOptOutResponse[]>({
    queryKey: ["perks", perkId, "opt-outs"],
    queryFn: () => apiRequest("GET", `/perks/${perkId}/opt-outs`),
    enabled: !!perkId,
  });
}
